import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { StaffSideMenu } from "../staff/StaffSideMenu";
import { useStaffAuthContext } from "../staff/useStaffAuthContext";
import { AthleteTabMenu } from "./AthleteTabMenu";
import AthleteProfileCard from "../component/AthleteProfileCard";
import API_URL from "../../api/API";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

export const AdminAthleteSpeedPerformance = () => {
  const navigate = useNavigate();
  const { staff } = useStaffAuthContext();
  const { athlete_id } = useParams();

  const [athlete, setAthlete] = useState(null);
  const [loadingAthlete, setLoadingAthlete] = useState(false);
  const [error, setError] = useState(null);

  // Performance filters
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [event, setEvent] = useState("100");
  const [intensity, setIntensity] = useState("98");

  // Performance data
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  /* ---------------- Fetch Athlete ---------------- */
  useEffect(() => {
    const fetchAthlete = async () => {
      setLoadingAthlete(true);
      try {
        const res = await fetch(`${API_URL.GET_ATHLETE_BY_ID}/${athlete_id}`, {
          headers: { Authorization: `Bearer ${staff.token}` },
        });
        const json = await res.json();

        if (res.ok) setAthlete(json);
        else setError(json.error || "Failed to load athlete");
      } catch (err) {
        setError(err.message);
      } finally {
        setLoadingAthlete(false);
      }
    };

    fetchAthlete();
  }, [athlete_id, staff.token]);

  /* ---------------- Fetch Performance ---------------- */
  const handleApplyFilter = async () => {
    if (!startDate || !endDate || !event) {
      alert("Please select Start Date, End Date and Event");
      return;
    }

    setLoading(true);
    try {
      const url =
        `${API_URL.ADMIN_COACHED_ATHLETE_GET_PERFORMANCE_DATA}` +
        `?athlete_id=${athlete_id}` +
        `&start_date=${startDate}` +
        `&end_date=${endDate}` +
        `&event=${event}` +
        `&intensity=${intensity}`;

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${staff.token}` },
      });

      const json = await res.json();
      if (!res.ok) {
        console.error(json);
        return;
      }

      setRows(json.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const chartData = rows.map((r) => ({
    ...r,
    distance: Number(r.distance),
    race_model_time: Number(r.race_model_time),
    actual_time: r.actual_time != null ? Number(r.actual_time) : null,
  }));

  /* ---------------- UI ---------------- */
  return (
    <div className="md:flex md:h-screen">
      <StaffSideMenu />

      {/* Scrollable content */}
      <div className="md:flex-1 bg-bgBase overflow-y-auto max-md:pt-4 pb-5">
        {/* Header */}
        <div className="bg-white shadow md:sticky md:top-0 z-30  -mb-4">
          {loadingAthlete ? (
            <div className="px-10 py-2 font-inter font-normal">
              <p className="text-sm text-gray-500">Loading athlete…</p>
            </div>
          ) : athlete ? (
            <AthleteProfileCard athlete={athlete} />
          ) : null}
        <AthleteTabMenu active="speed-performance" />
        </div>


        {/* Performance Card */}
        <div className="pt-10 px-5 md:px-8 xl:px-16">
          <div className="bg-white border border-tblBorder p-5 md:p-6 rounded-14 w-full max-w-[1336px]">
            <div className="space-y-3 mb-4">
              <h2 className="text-dark font-inter text-xl font-medium">
                Race Model Analysis
              </h2>
              <p className="text-slate100 font-inter font-normal text-base">
                Compare actual performance against target race model projection
              </p>
            </div>
            <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
              <div className="max-xl:order-2 xl:col-span-8">
                {/* GRAPH: Race Model vs Best Actual */}
                {chartData.length > 0 && (
                <div className="bg-white border border-tblBorder rounded-14 p-4 xl:p-6 mb-6">
                  <p className="text-dark font-inter font-medium text-sm mb-4">
                    Race Model vs Best Actual
                  </p>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart
                      data={chartData}
                      margin={{ top: 10, right: 20, left: -10, bottom: 0 }}
                    >
                      <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" />

                      <XAxis
                        dataKey="distance"
                        tick={{
                          fill: "#6b7280",
                          fontSize: 12,
                          fontFamily: "Inter",
                        }}
                        axisLine={false}
                        tickLine={false}
                      />

                      <YAxis
                        tick={{
                          fill: "#6b7280",
                          fontSize: 12,
                          fontFamily: "Inter",
                        }}
                        axisLine={false}
                        tickLine={false}
                      />

                      <Tooltip
                        contentStyle={{
                          backgroundColor: "#fff",
                          border: "1px solid #e5e7eb",
                          borderRadius: "6px",
                          fontFamily: "Inter",
                          fontSize: 12,
                        }}
                      />

                      {/* Race Model */}
                      <Line
                        type="monotone"
                        dataKey="race_model_time"
                        name="Race Model"
                        stroke="#111827"
                        strokeWidth={2.5}
                        dot={{ r: 4, fill: "#111827" }}
                      />

                      {/* Actual */}
                      <Line
                        type="monotone"
                        dataKey="actual_time"
                        name="Best Actual"
                        stroke="#c19e42"
                        strokeWidth={2}
                        dot={{ r: 3 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                )}

                {/* Table */}
                {rows.length === 0 ? (<></>
                  
                ) : (

                <div className="bg-white border border-tblBorder rounded-14 p-4 xl:p-6">
                  <p className="text-dark font-inter font-medium text-sm mb-4">
                    Split Times
                  </p>

                  <div className="overflow-x-auto">
                    <table className="table-auto w-full">
                      <thead className="sticky top-0 border-b border-tblBorder">
                        <tr className="text-slate100 font-inter !font-medium text-xs md:text-sm uppercase">
                          <th className="py-2 text-left">Distance (m)</th>
                          <th className="py-2 text-left">Actual</th>
                          <th className="py-2 text-left">Target</th>
                          <th className="py-2 text-left">Difference</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-tblBorder">
                        {loading ? (
                          <tr>
                            <td
                              colSpan="3"
                              className="px-4 py-6 text-center text-gray-500"
                            >
                              Loading…
                            </td>
                          </tr>
                        ) : rows.length === 0 ? (
                          <tr>
                            <td
                              colSpan="3"
                              className="px-4 py-6 text-center text-gray-500"
                            >
                              No data available
                            </td>
                          </tr>
                        ) : (
                          rows.map((r, i) => (
                            <tr
                              key={i}
                              className="text-dark font-inter font-normal text-sm"
                            >
                              <td className="py-2">{r.distance}</td>
                              <td className="py-2">{r.actual_time ?? "-"}</td>
                              <td className="py-2 font-medium">
                                {r.race_model_time}
                              </td>
                              <td
                                className={`py-2 ${
                                  r.actual_time != null &&
                                  r.race_model_time != null
                                    ? r.actual_time - r.race_model_time > 0
                                      ? "text-red-500"
                                      : "text-green-600"
                                    : ""
                                }`}
                              >
                                {r.actual_time != null &&
                                r.race_model_time != null
                                  ? (r.actual_time - r.race_model_time).toFixed(
                                      2,
                                    )
                                  : "-"}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                ) }

                {error && <p className="text-red-600 text-sm mt-3">{error}</p>}
              </div>
              {/* Filters */}
              <div className="max-xl:order-1 xl:col-span-4">
                <div className="bg-white border border-tblBorder rounded-14 p-4 xl:p-6">
                  <div className="flex flex-col gap-3 admindate">
                    <div>
                      <label class="block font-inter text-slate100 text-sm font-medium capitalize mb-2">
                        start date
                      </label>
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="w-full border border-tblBorder p-3 rounded-10 placeholder:text-slate100 text-sm font-inter font-normal leading-tight"
                      />
                    </div>
                    <div>
                      <label class="block font-inter text-slate100 text-sm font-medium capitalize mb-2">
                        last date
                      </label>
                      <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="w-full border border-tblBorder p-3 rounded-10 placeholder:text-slate100 text-sm font-inter font-normal leading-tight"
                      />
                    </div>
                    <div>
                      <label class="block font-inter text-slate100 text-sm font-medium capitalize mb-2">
                        event
                      </label>
                      <select
                        value={event}
                        onChange={(e) => setEvent(e.target.value)}
                        className="w-full outline-none bg-white border border-tblBorder p-3 rounded-10 text-sm font-inter font-normal leading-tight"
                      >
                        <option value="100">100m</option>
                        <option value="200">200m</option>
                      </select>
                    </div>
                  </div>

                  {/* Intensity Toggle */}
                  <div className="mt-4">
                    <p className="font-inter font-medium text-slate100 text-xs mb-2">
                      Race Model Intensity
                    </p>
                    <div className="flex flex-col gap-2">
                      <label className="cursor-pointer flex items-center gap-2">
                        <input
                          type="radio"
                          name="intensity"
                          value="98"
                          checked={intensity === "98"}
                          onChange={(e) => setIntensity(e.target.value)}
                          className="accent-black"
                        />
                        <span className="text-dark font-inter font-normal text-sm">
                          98%
                        </span>
                      </label>

                      <label className="cursor-pointer flex items-center gap-2">
                        <input
                          type="radio"
                          name="intensity"
                          value="95"
                          checked={intensity === "95"}
                          onChange={(e) => setIntensity(e.target.value)}
                          className="accent-black"
                        />
                        <span className="text-dark font-inter font-normal text-sm">
                          95%
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="mt-4">
                    <button
                      onClick={handleApplyFilter}
                      className="bg-dark w-full text-center text-white px-6 py-2 rounded-10 font-inter font-medium text-sm hover:bg-black"
                    >
                      Apply
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAthleteSpeedPerformance;
